import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

/**
 * Compact pagination bar used everywhere a list surface holds more than
 * ~30 rows. Purely presentational — the caller owns the page state and
 * decides whether to slice a client-side array or refetch server-side.
 *
 * Props:
 *   page          — current 1-based page number
 *   totalPages    — total page count (0 renders a disabled bar)
 *   onPage(next)  — callback fired when a page is chosen
 *   totalItems    — total row count (for the "n of m" caption)
 *   pageSize      — rows per page (for the "n of m" caption)
 *   pageSizeOptions — optional list of selectable page sizes
 *   onPageSize(next) — required when pageSizeOptions is supplied
 *   testidPrefix  — e.g. "molecules" → data-testid="molecules-pager-next"
 */
export default function TablePager({
  page = 1,
  totalPages = 0,
  onPage,
  totalItems = 0,
  pageSize = 30,
  pageSizeOptions,
  onPageSize,
  testidPrefix = "table",
}) {
  if (totalPages <= 1 && totalItems <= pageSize) return null;

  const clamp = (n) => Math.max(1, Math.min(n, totalPages || 1));
  const go = (n) => onPage && onPage(clamp(n));
  const isFirst = page <= 1;
  const isLast = page >= (totalPages || 1);

  // Build a compact page window: 1 … p-1 p p+1 … totalPages
  const win = [];
  const push = (n) => win.push(n);
  const total = totalPages || 1;
  if (total <= 7) {
    for (let i = 1; i <= total; i++) push(i);
  } else {
    push(1);
    if (page > 3) push("…");
    for (let i = Math.max(2, page - 1); i <= Math.min(total - 1, page + 1); i++) push(i);
    if (page < total - 2) push("…");
    push(total);
  }

  const from = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);

  return (
    <div
      className="mt-4 flex items-center justify-between flex-wrap gap-3 border-t border-border pt-3"
      data-testid={`${testidPrefix}-pager`}
    >
      <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
        {from}–{to} of {totalItems}
      </div>
      <div className="flex flex-wrap items-center gap-1 min-w-0 max-w-full">
        <button
          type="button"
          onClick={() => go(1)}
          disabled={isFirst}
          className="p-1.5 border border-border hover:border-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          aria-label="First page"
          data-testid={`${testidPrefix}-pager-first`}
        >
          <ChevronsLeft size={14} strokeWidth={1.75} />
        </button>
        <button
          type="button"
          onClick={() => go(page - 1)}
          disabled={isFirst}
          className="p-1.5 border border-border hover:border-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          aria-label="Previous page"
          data-testid={`${testidPrefix}-pager-prev`}
        >
          <ChevronLeft size={14} strokeWidth={1.75} />
        </button>
        {win.map((it, i) =>
          it === "…" ? (
            <span key={`e${i}`} className="px-2 text-muted-foreground text-xs">…</span>
          ) : (
            <button
              key={it}
              type="button"
              onClick={() => go(it)}
              className={`min-w-[32px] px-2 py-1 text-xs font-mono border transition-colors ${
                it === page
                  ? "bg-foreground text-background border-foreground"
                  : "border-border hover:border-foreground"
              }`}
              aria-current={it === page ? "page" : undefined}
              data-testid={`${testidPrefix}-pager-page-${it}`}
            >
              {it}
            </button>
          ),
        )}
        <button
          type="button"
          onClick={() => go(page + 1)}
          disabled={isLast}
          className="p-1.5 border border-border hover:border-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          aria-label="Next page"
          data-testid={`${testidPrefix}-pager-next`}
        >
          <ChevronRight size={14} strokeWidth={1.75} />
        </button>
        <button
          type="button"
          onClick={() => go(total)}
          disabled={isLast}
          className="p-1.5 border border-border hover:border-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          aria-label="Last page"
          data-testid={`${testidPrefix}-pager-last`}
        >
          <ChevronsRight size={14} strokeWidth={1.75} />
        </button>
        {pageSizeOptions && onPageSize && (
          <select
            aria-label="Rows per page"
            value={pageSize}
            onChange={(e) => onPageSize(parseInt(e.target.value, 10))}
            className="ml-3 border border-border px-2 py-1 text-xs font-mono bg-background hover:border-foreground transition-colors"
            data-testid={`${testidPrefix}-pager-pagesize`}
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>{n} / page</option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}
